import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor, CapacitorCookies } from '@capacitor/core';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { resetAppState } from 'src/app/core/store/global/global.actions';
import { PushNofiticationsService } from '../../communication/push-nofitications.service';
import { TokenStorageService } from '../tokens/token-storage.service';

@Injectable({
  providedIn: 'root',
})
export class CleanupService {
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly pushNotificationsService = inject(PushNofiticationsService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly apollo = inject(Apollo);

  async performLocalLogout(returnUrl?: string): Promise<void> {
    await this.pushNotificationsService.unregisterPushNotifications();
    this.tokenStorage.clear();
    this.apollo.client.clearStore();
    void this.clearNativeCookies();
    this.clearAllCookies();
    localStorage.clear();
    sessionStorage.clear();
    this.broadcastLogout();
    this.store.dispatch(resetAppState());

    // Only navigate to /login with NO returnUrl on logout
    this.router.navigate(['/login']);
    return;
  }

  private broadcastLogout(): void {
    localStorage.setItem('logout-event', Date.now().toString());
  }

  private clearAllCookies(): void {
    if (typeof document === 'undefined' || typeof location === 'undefined') {
      return;
    }

    const cookies = document.cookie ? document.cookie.split(';') : [];
    if (cookies.length === 0) {
      return;
    }

    const hostname = location.hostname;
    const domainParts = hostname.split('.');

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const rawName = eqPos > -1 ? cookie.slice(0, eqPos) : cookie;
      const name = rawName.trim();
      if (!name) {
        return;
      }

      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

      if (domainParts.length > 1) {
        for (let i = 0; i < domainParts.length - 1; i += 1) {
          const domain = `.${domainParts.slice(i).join('.')}`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
        }
      }
    });
  }

  private async clearNativeCookies(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await CapacitorCookies.clearAllCookies();
    } catch (error) {
      console.warn('clearNativeCookies() Failed to clear native cookies:', error);
    }
  }
}
